import './CreateNewSubClusterModal.less';

import {
  Form,
  Button,
  Col,
  Divider,
  Input,
  InputNumber,
  Row,
  Select,
} from 'antd';
import React, { useState, useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Rule } from 'antd/lib/form';
import {
  clusterNameRules,
  getClusterTypeName,
  getClusterTypeIcons,
} from 'utils/cluster';
import { Instance, SubCluster } from 'types/cluster';
import { SessionContext } from 'auth/SessionProvider';
import {
  useClusterSizeTypes,
  useRequestCreateSubCluster,
  useUpdateSubCluster,
} from 'hooks/cluster';

const MIN = 1;
const MAX = 10;

export const createNewSubClusterModalFieldNames = {
  name: 'name',
  nodes: 'nodes',
  size: 'size',
} as const;

interface CreateNewSubClusterModalProps {
  onCancel(): void;
  instance: Instance;
  subCluster?: SubCluster;
}

function CreateNewSubClusterModal({
  instance,
  subCluster,
  onCancel,
}: CreateNewSubClusterModalProps) {
  const { user } = useContext(SessionContext);
  const intl = useIntl();
  const [form] = Form.useForm();

  const { validateFields } = form;

  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(true);

  const {
    mutateAsync: requestCreateSubCluster,
    isLoading: createSubClusterLoading,
  } = useRequestCreateSubCluster({ refetchClusterList: true });
  const {
    mutateAsync: requestUpdateSubCluster,
    isLoading: updateSubClusterLoading,
  } = useUpdateSubCluster({ refetchClusterList: true });

  const { data: imagesData } = useClusterSizeTypes();
  const sizes = useMemo(() => imagesData?.data.sizes ?? [], [imagesData]);
  const size = sizes?.length === 0 ? null : subCluster?.size ?? sizes[0].type;

  async function handleSubmit(values: any) {
    try {
      await validateFields();
      if (subCluster) {
        // update sub Cluster
        await requestUpdateSubCluster({
          userId: user.uuid,
          clusterName: instance.name,
          data: {
            subCluster: subCluster.name,
            size: values.size,
            nodes: values.nodes,
          },
        });
      } else {
        // create new one
        await requestCreateSubCluster({
          userId: user.uuid,
          clusterName: instance.name,
          data: {
            type: 'analytics',
            size: values.size,
            name: values.name,
            nodes: values.nodes,
          },
        });
      }
      onCancel();
    } catch {}
  }

  const loading = createSubClusterLoading || updateSubClusterLoading;

  const onValuesChange = async (changedValues: any, allValues: any) => {
    const { name, nodes, size } = form.getFieldsValue();
    if (
      (subCluster && (!nodes || !size)) ||
      (!subCluster && (!name || !nodes || !size))
    ) {
      setIsSubmitButtonDisabled(true);
    } else {
      setIsSubmitButtonDisabled(false);
    }
  };

  return (
    <div className="CreateNewSubClusterModal modal-body">
      <p className="CreateNewSubClusterModal__desc">
        <FormattedMessage id={'createNewSubClusterModal.description'} />
      </p>

      <Form
        form={form}
        layout="vertical"
        id="ChangeLoadersModal"
        onFinish={handleSubmit}
        onValuesChange={onValuesChange}
      >
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name={createNewSubClusterModalFieldNames.name}
              label={
                <FormattedMessage
                  id={'createNewSubClusterModal.nameFieldLabel'}
                />
              }
              rules={subCluster ? [] : (clusterNameRules as Rule[])}
            >
              {subCluster ? (
                <Input defaultValue={subCluster.name} disabled />
              ) : (
                <Input
                  placeholder={intl.formatMessage({
                    id: 'createNewSubClusterModal.nameFieldPlaceholder',
                  })}
                />
              )}
            </Form.Item>
          </Col>

          <Col xs={24} sm={12}>
            <Form.Item
              name={createNewSubClusterModalFieldNames.size}
              label={
                <FormattedMessage id="createNewSubClusterModal.sizeFieldLabel" />
              }
              initialValue={size}
              rules={[
                {
                  required: true,
                  message: (
                    <FormattedMessage id="clusterForm.clusterSizeRequired" />
                  ),
                },
              ]}
            >
              <Select
                showSearch
                loading={sizes?.length === 0}
                placeholder={intl.formatMessage({
                  id: 'createNewSubClusterModal.sizeFieldPlaceholder',
                })}
              >
                {sizes?.map(size => (
                  <Select.Option key={size.type} value={size.type}>
                    {React.createElement(
                      getClusterTypeIcons(size.type, 'fill'),
                      {
                        className: 'fill-icon',
                        style: { fontSize: 14, color: '#99A6B0' },
                      },
                    )}{' '}
                    {getClusterTypeName(size.type)}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Divider style={{ margin: 0 }} />

        <div className="CreateNewSubClusterModal__field">
          <Form.Item
            name={createNewSubClusterModalFieldNames.nodes}
            label={
              <FormattedMessage id="createNewSubClusterModal.nodesFieldLabel" />
            }
            initialValue={subCluster?.nodes ?? 1}
            rules={[
              {
                required: true,
                message: (
                  <FormattedMessage id="createNewSubClusterModal.nodesFieldError" />
                ),
              },
            ]}
          >
            <InputNumber style={{ width: 290 }} min={MIN} max={MAX} />
          </Form.Item>
        </div>

        <div className="modal-body-footer">
          <Button
            htmlType="submit"
            type="primary"
            disabled={
              isSubmitButtonDisabled ||
              form.getFieldsError().some(err => err.errors.length)
            }
            loading={loading}
          >
            {subCluster ? (
              <FormattedMessage id={'createNewSubClusterModal.saveButton'} />
            ) : (
              <FormattedMessage id={'createNewSubClusterModal.createButton'} />
            )}
          </Button>
          <Button onClick={onCancel}>
            <FormattedMessage id={'createNewSubClusterModal.cancelButton'} />
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default CreateNewSubClusterModal;
